import { create } from 'zustand'
import { feedbackApi } from '@api/client'
import type { FeedbackTicket, SubmitFeedbackInput, SubmitFeedbackResult, KanbanStatus } from './types'

interface FeedbackState {
  tickets: FeedbackTicket[]
  isLoading: boolean
  isSubmitting: boolean
  error: string | null

  // Actions
  loadTickets: () => Promise<void>
  submitFeedback: (input: SubmitFeedbackInput) => Promise<SubmitFeedbackResult | null>
  updateTicketStatus: (id: number, status: KanbanStatus) => Promise<void>
  updateTicket: (id: number, updates: Partial<FeedbackTicket>) => Promise<void>
  deleteTicket: (id: number) => Promise<boolean>
}

export const useFeedbackStore = create<FeedbackState>()((set, get) => ({
  tickets: [],
  isLoading: false,
  isSubmitting: false,
  error: null,

  loadTickets: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await feedbackApi.list()
      if (response.success && response.data) {
        set({ tickets: response.data.tickets, isLoading: false })
      } else {
        set({ error: response.error || 'Failed to load tickets', isLoading: false })
      }
    } catch {
      set({ error: 'Failed to load tickets', isLoading: false })
    }
  },

  submitFeedback: async (input: SubmitFeedbackInput) => {
    set({ isSubmitting: true, error: null })
    try {
      const response = await feedbackApi.submit(input)
      if (response.success && response.data) {
        // Refresh tickets list
        await get().loadTickets()
        set({ isSubmitting: false })
        return response.data
      } else {
        set({ error: response.error || 'Failed to submit feedback', isSubmitting: false })
        return null
      }
    } catch {
      set({ error: 'Failed to submit feedback', isSubmitting: false })
      return null
    }
  },

  updateTicketStatus: async (id: number, status: KanbanStatus) => {
    // Optimistic update
    const previousTickets = get().tickets
    set({
      tickets: previousTickets.map(t =>
        t.id === id ? { ...t, status } : t
      ),
    })

    try {
      const response = await feedbackApi.updateStatus(id, status)
      if (!response.success) {
        // Revert on failure
        set({ tickets: previousTickets })
      }
    } catch {
      // Revert on failure
      set({ tickets: previousTickets })
    }
  },

  updateTicket: async (id: number, updates: Partial<FeedbackTicket>) => {
    try {
      const response = await feedbackApi.update(id, updates)
      if (response.success && response.data) {
        set({
          tickets: get().tickets.map(t =>
            t.id === id ? { ...t, ...response.data } : t
          ),
        })
      }
    } catch {
      console.error('Failed to update ticket')
    }
  },

  deleteTicket: async (id: number) => {
    try {
      const response = await feedbackApi.delete(id)
      if (response.success) {
        set({ tickets: get().tickets.filter(t => t.id !== id) })
        return true
      }
      return false
    } catch {
      console.error('Failed to delete ticket')
      return false
    }
  },
}))
